import {
  SupportRequestSocketItem,
  SupportRequestSocketRequest,
} from './SupportRequestSocket';
import { Card } from '~/components/Card/Card';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { getInitials } from '~/components/Initials';
import { View } from '@droplet-tech-code/core-elements/module/ui/View';
import { Text } from '@droplet-tech-code/core-elements/module/ui/Text';
import { ColorKeys } from '@droplet-tech-code/core-elements/module/theme';
import { Pressable } from 'react-native';
import { translate } from '~/utils/translation.utils';
import {
  displaySupportStatus,
  getColorFromStatus,
} from '~/components/SupportRequest/utils/SupportRequestStatus.utils';
import { SupportStatus } from '@naus-code/naus-client-types';
import { SupportRequestBackendClient } from '@naus-code/naus-admin-types';
import { AppText } from '~/components/Text';
import { LiveDateText } from '~/components/DateText';
import { Icon } from '@droplet-tech-code/core-elements/module/ui/Icon';
import { dayjs } from '@droplet-tech-code/core-elements/module/utils/utils.date';
import { stringToColour } from '../../SupportRequest.ListItem';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useSupportRequestStore } from '../../utils/supportRequest.store';
import { Flipped, Flipper } from 'react-flip-toolkit';

export const PendingSupportRequestListItem = ({
  groupId,
  isActive,
}: {
  groupId: string;
  isActive: boolean;
}) => {
  const [expanded, setExpanded] = useState(true);
  const selectedId = useSupportRequestStore((state) => state.selectedRequestId);
  const requests = useSupportRequestStore(
    (state) => {
      if (isActive) {
        return state.activeMap[groupId];
      }
      return state.inactiveMap[groupId];
    },
    (a, b) => a?.join() === b?.join(),
  );
  if (!requests) {
    return null;
  }
  return (
    <Flipper flipKey={expanded} spring="gentle">
      <Flipped flipId={groupId}>
        <div>
          <Card style={{ borderRadius: 5, flex: 1, width: 276 }}>
            <VStack flex={1}>
              <Pressable
                onPress={() => setExpanded(!expanded)}
                disabled={requests.length < 2}
              >
                <ListItemHeader groupId={groupId} />
              </Pressable>
              <VStack style={{ gap: 5 }}>
                {requests.map((requestId) => {
                  const isSelected = requestId === selectedId;
                  return (
                    <div
                      style={{
                        border: '1.2px solid',
                        borderColor: isSelected ? '#0018f5ff' : 'transparent',
                        borderTopRightRadius: 5,
                        borderBottomRightRadius: 5,
                        transition: 'border-color 0.3s ease',
                      }}
                      key={requestId}
                    >
                      <InternalRequest
                        key={requestId}
                        collapsed={!expanded}
                        requestId={requestId}
                      />
                    </div>
                  );
                })}
              </VStack>
            </VStack>
          </Card>
        </div>
      </Flipped>
    </Flipper>
  );
};

function getGroupKey(supporRequestGroup?: SupportRequestSocketItem) {
  if (!supporRequestGroup) {
    return '';
  }
  return `${supporRequestGroup.p_customerName}-${supporRequestGroup.p_email}-${supporRequestGroup.requests.map((req) => req.id).join()}`;
}

export function getSupportKey(request?: SupportRequestSocketRequest) {
  if (!request) {
    return '';
  }
  //@ts-expect-error
  return `${request._adminUserId}${request._adminUserIdSupervisor}${request.dateActionBy}${request.dateUpdated}${request.feedback}${request.vi}${request.status}${request.dateLastReplied}${request.dateLastSeen}${request.dateRegarding}${request.pendingRefund}${request.pendingOp}${request.paymentSession}${request.supportTypeTitle}${request.requiresAttention}${request.supportTypeTitle}`;
}

const ListItemHeader = ({ groupId }: { groupId: string }) => {
  const supportRequest = useSupportRequestStore(
    (state) => state.groupMap[groupId],
    (a, b) => getGroupKey(a) === getGroupKey(b),
  );

  if (!supportRequest) {
    return null;
  }
  const { p_customerName, p_email } = supportRequest;

  return (
    <HStack style={{ paddingRight: 5, paddingLeft: 5, height: 22 }} space="2.5">
      <Initials name={p_customerName} />
      <Text.ExtraSmall
        style={{ fontSize: 10 }}
        color="backdrop"
        align="right"
        numberOfLines={1}
        flex={1}
      >{`${p_customerName}, ${p_email}`}</Text.ExtraSmall>
    </HStack>
  );
};

const Initials = ({
  name,
  isInactive,
}: {
  name?: string | null;
  isInactive?: boolean;
}) => {
  if (!name) {
    return (
      <View
        style={{
          width: 14,
          height: 14,
          borderStyle: 'dashed',
          borderWidth: 1,
          borderColor: '#B0B0B0',
          backgroundColor: '##E9E9E9',
        }}
        br={20}
        justify="center"
        align="center"
        p="1"
      ></View>
    );
  }

  const color = name ? stringToColour(name, 20) : 'white';
  const initials = getInitials(name);

  return (
    <View
      style={{
        width: 16,
        height: 16,
        backgroundColor: color,
        opacity: isInactive ? 0.5 : 1,
      }}
      br={20}
      justify="center"
      align="center"
      p="1"
    >
      <Text.Body1Medium style={{ fontSize: 10 }} color="monochrome-dark">
        {initials}
      </Text.Body1Medium>
    </View>
  );
};

const InternalRequest = ({
  requestId,
  collapsed,
}: {
  requestId: string;
  collapsed?: boolean;
}) => {
  const request = useSupportRequestStore(
    (state) => state.requestMap[requestId],
    (a, b) => getSupportKey(a) === getSupportKey(b),
  );
  if (!request) {
    return null;
  }
  const setSelectedRequestId = useSupportRequestStore(
    (state) => state.setSelectedRequestId,
  );

  return (
    <Pressable
      onPress={() => {
        setSelectedRequestId(request.id);
      }}
    >
      <HStack
        style={{
          backgroundColor: '#F8F8FA',
          display: 'flex',
          alignItems: 'stretch',
          borderRadius: 5,
        }}
      >
        <Bar color={request.color as ColorKeys} />
        <RequestDetailsWrapper collapsed={collapsed} request={request} />
      </HStack>
    </Pressable>
  );
};

const Bar = ({ color }: { color: ColorKeys }) => {
  return (
    <VStack
      style={{
        backgroundColor: color,
        opacity: 1,
        flex: 1,
        maxWidth: 5,
        borderTopRightRadius: 5,
        borderBottomRightRadius: 5,
      }}
    />
  );
};

const RequestDetailsWrapper = ({
  request,
  collapsed,
}: {
  request: SupportRequestSocketRequest;
  collapsed?: boolean;
}) => {
  const [flash, setFlash] = useState(false);
  const easeRef = useRef(0.5);
  const countRef = useRef(0);
  useEffect(() => {
    const interval = setInterval(() => {
      if (checkIfRecent(request, countRef.current)) {
        setFlash((flash) => !flash);
        countRef.current++;
        return;
      }
      easeRef.current = 100;
      setFlash(false);
      clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const boxStyle = {
    backgroundColor: toggleColor(flash),
    flex: 1,
    borderRadius: 5,
    transition: `background-color ${easeRef.current}s ease`,
  };

  return (
    <div style={boxStyle}>
      <View flex={1}>
        <RequestDetails request={request} collapsed={collapsed} />
      </View>
    </div>
  );
};

function checkIfRecent(request: SupportRequestSocketRequest, count: number) {
  return false;

  if (count > 6) {
    return false;
  }
  const diffMins = dayjs().diff(request.dateCreated, 'minutes');

  if (request.status != 'Pending') {
    return false;
  }
  if (diffMins < 10) {
    return true;
  }
  return false;
}

function toggleColor(flash: boolean) {
  if (flash) {
    return '#f7e0b8ff';
  }
  return '#F8F8FA';
}

const RequestDetails = ({
  request,
  collapsed,
}: {
  request: SupportRequestSocketRequest;
  collapsed?: boolean;
}) => {
  if (collapsed) {
    return (
      <VStack flex={1} style={{ paddingVertical: 2, paddingHorizontal: 5 }} space="0.5">
        <InternalTitleSection request={request} />
      </VStack>
    );
  }
  return (
    <VStack flex={1} style={{ paddingVertical: 2, paddingHorizontal: 5 }} space="0.5">
      <InternalTitleSection request={request} />
      <InternalDetailSection request={request} />
      <InternalBadgeSection request={request} />
    </VStack>
  );
};

const InternalTitleSection = ({ request }: { request: SupportRequestSocketRequest }) => {
  const { supportTypeTitle, dateCreated, status, ref, adminUserName } = request;
  const isInactive = useSupportRequestStore((state) => state.requestMap[request.id]);
  return (
    <HStack space="1" flex={1} style={{ width: '100%' }}>
      <VStack>
        <Text.Small
          color="black"
          style={{ fontSize: 12, fontWeight: '800', lineHeight: 18, maxWidth: 120 }}
          numberOfLines={1}
        >
          {supportTypeTitle}
        </Text.Small>
        <Text.Small color="monochrome-mid" style={{ fontSize: 8, lineHeight: 10 }}>
          {dayjs(dateCreated).format('ddd DD-MMM-YY')}
        </Text.Small>
      </VStack>
      <View flex={1} />
      {status === 'Pending' && (
        <SquarePill
          text={displaySupportStatus(
            request as unknown as SupportRequestBackendClient,
          ).toUpperCase()}
          bgColor="#FF7E47"
          fColor="white"
        />
      )}
      <AppText.ExtraSmall color="monochrome-dark">{`#${ref.slice(-4)}`}</AppText.ExtraSmall>
      <Initials name={adminUserName} isInactive={!isInactive?.isActive} />
      <StatusCircle status={status} />
    </HStack>
  );
};

const StatusCircle = ({ status }: { status: SupportStatus }) => {
  const statusColor = getColorFromStatus(status);
  if (status === 'Open' || status === 'Pending') {
    return (
      <View
        style={{ width: 14, height: 14 }}
        br={999}
        b={1}
        bc={statusColor}
        bg="white"
        justify="center"
        align="center"
      ></View>
    );
  }
};

export const SquarePill = ({
  text,
  bgColor,
  fColor,
  onPress,
  size = 'small',
}: {
  text: string;
  bgColor: string;
  fColor: string;
  onPress?: () => void;
  size?: 'small' | 'medium' | 'large';
}) => {
  const fontSize = (() => {
    switch (size) {
      case 'small':
        return 10;
      case 'medium':
        return 14;
      case 'large':
        return 18;
    }
  })();
  return (
    <Pressable disabled={!onPress} onPress={onPress}>
      <View
        style={{
          backgroundColor: bgColor,
          alignContent: 'center',
          justifyContent: 'center',
          height: fontSize + 6,
        }}
        px="1"
        py="0.5"
        br={4}
      >
        <Text.Small
          style={{
            fontSize,
            fontWeight: 'bold',
            lineHeight: fontSize,
            alignContent: 'center',
            justifyContent: 'center',
            color: fColor,
          }}
        >
          {text}
        </Text.Small>
      </View>
    </Pressable>
  );
};

const InternalDetailSection = ({ request }: { request: SupportRequestSocketRequest }) => {
  const { dateActionBy, dateRegarding } = request;
  return (
    <VStack>
      {dateActionBy && (
        <HStack>
          <Text.ExtraSmall
            flex={1}
            color="monochrome-mid"
            align="left"
            style={{ fontSize: 10, lineHeight: 18, fontWeight: '600' }}
          >
            {translate('supportRequests.actionBy')}
          </Text.ExtraSmall>
          <LiveDateText
            dateTime={dateActionBy}
            indexTextStyles={[{ style: { fontSize: 10 } }, { bold: true }]}
            formatOption="fullDate&duration"
            type="extraSmall"
            color="monochrome-dark"
            align="left"
          />
        </HStack>
      )}
      {dateRegarding && (
        <HStack>
          <Text.ExtraSmall
            flex={1}
            color="monochrome-mid"
            align="left"
            bold
            style={{ fontSize: 10, lineHeight: 18, fontWeight: '600' }}
          >
            {translate('supportRequests.dateRegarding')}
          </Text.ExtraSmall>
          <LiveDateText
            dateTime={dateRegarding}
            indexTextStyles={[{ style: { fontSize: 10 } }, { bold: true }]}
            formatOption="fullDate&duration"
            type="extraSmall"
            color="monochrome-dark"
            align="left"
          />
        </HStack>
      )}
    </VStack>
  );
};

const InternalBadgeSection = ({ request }: { request: SupportRequestSocketRequest }) => {
  const {
    //Badges
    feedback,
    cfar,
    koala,
    vi,
    pendingOp,
    pendingRefund,
    priority,
    //Seen
    dateLastReplied,
    dateLastSeen,
    paymentComplete,
    pendingPayment,
  } = request;
  const hasSubsection =
    !!feedback ||
    !!pendingPayment ||
    !!cfar ||
    !!koala ||
    !!vi ||
    !!pendingOp ||
    !!priority ||
    !!dateLastReplied ||
    !!dateLastSeen;
  if (!hasSubsection) {
    return null;
  }
  return (
    <HStack space="4" style={{ paddingHorizontal: 5 }}>
      {priority && (
        <SquarePill
          text={translate('supportRequests.prioritySupport').toUpperCase()}
          bgColor="#FDF1CE"
          fColor="#A4631B"
        />
      )}
      {cfar && <SquarePill text="CFAR" bgColor="#FDC9B3" fColor="#F61F11" />}
      {koala && <SquarePill text="KOALA" bgColor="#c5d2f4ff" fColor="#2762f6ff" />}
      {vi && <SquarePill text="VI" bgColor="#E2CFFF" fColor="#5123D3" />}
      {pendingOp && (
        <SquarePill
          text={translate('supportRequests.pendingOperatorResponse')}
          bgColor="#D4EFD5"
          fColor="#47991D"
        />
      )}
      {pendingRefund && (
        <Badge>
          <Icon size="small" color="monochrome-extraDark" icon="custom-refundable" />
        </Badge>
      )}
      {feedback && (
        <Badge>
          <Icon size="small" color="monochrome-extraDark" icon="rating" />
        </Badge>
      )}
      {pendingPayment && (
        <Badge>
          <AppText.ExtraSmall
            align="center"
            color="monochrome-extraDark"
            style={{ height: 14, lineHeight: 14, fontSize: 10 }}
            bold
          >
            {'$'}
          </AppText.ExtraSmall>
        </Badge>
      )}
      {paymentComplete && (
        <Badge bgColor="#D4EFD5">
          <AppText.ExtraSmall
            align="center"
            color="monochrome-extraDark"
            style={{ height: 14, lineHeight: 14, fontSize: 10, color: '#47991D' }}
            bold
          >
            {'$'}
          </AppText.ExtraSmall>
        </Badge>
      )}
      <View flex={1} />
      <Seen dateLastSeen={dateLastSeen} />
      <LastReplied dateLastReplied={dateLastReplied} />
    </HStack>
  );
};

const Seen = ({ dateLastSeen }: { dateLastSeen?: string }) => {
  if (!dateLastSeen) {
    return null;
  }
  return (
    <HStack space="1">
      <Icon
        icon="eye"
        size="small"
        color="primary-mid"
        iconStyle={{ height: 12, width: 12 }}
      />
      <LiveDateText
        align="center"
        dateTime={dateLastSeen}
        formatOption="lifetimeDependent&UnderDay"
        type="extraSmall"
        style={{ fontSize: 10, lineHeight: 10 }}
        fontWeight="700"
        color="primary-mid"
        defaultText="seen"
      />
    </HStack>
  );
};

const LastReplied = ({ dateLastReplied }: { dateLastReplied?: string }) => {
  if (!dateLastReplied) {
    return null;
  }
  const diff = dayjs().diff(dateLastReplied, 'm');
  const diffh = dayjs().diff(dateLastReplied, 'h');
  const color =
    diff < 5 ? 'monochrome-dark' : diff < 60 ? 'warning' : diff < 360 ? 'error' : 'error';
  if (color === undefined) {
    return;
  }

  const timeText = diffh === 0 ? `${diff}m ago` : `${diffh}h ago`;
  return (
    <HStack space="1">
      <Icon icon="feedback" size="small" color={color} />
      <AppText.ExtraSmall
        style={{ fontSize: 10, lineHeight: 10, fontWeight: '700' }}
        color={color}
      >
        {timeText}
      </AppText.ExtraSmall>
    </HStack>
  );
};

const Badge = ({ children, bgColor }: { children: ReactNode; bgColor?: string }) => {
  return (
    <View
      bg="monochrome-light"
      style={{
        width: 22,
        height: 16,
        borderRadius: 4,
        alignContent: 'center',
        justifyContent: 'center',
        backgroundColor: bgColor,
      }}
    >
      {children}
    </View>
  );
};
